import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button
} from "reactstrap";
import { useConversation } from "@11labs/react";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { useSelector } from "react-redux";

const MAX_PROMPT_CHARS = 6000;

const DEFAULT_SCENARIO_PROMPT = `You are Aria, a Food & Beverage Server Performance AI Coach training about the "{scenario}". You will lead a roleplay to train this scenario based on these key points:

{flashcardContent}

The employee is the SERVER. You are the GUEST.

Start by briefly explaining the scenario, the employees role, the objective and they key points they should demonstrate using {flashcardContent}.

Keep this introduction short. Then say the roleplay is starting and immediately become the guest.

ROLEPLAY:
Stay in character as the guest and have a realistic back-and-forth conversation.

Respond naturally to what the employee says. Keep your turns short and conversational. Never speak for the employee.

Give the employee opportunities to demonstrate the key points from the flashcard. Ask questions, hesitate or raise realistic objections when appropriate.
If the employee struggles, briefly step out of character and give a small coaching hint. Do not give them the full answer. Then continue as the guest.
ENDING:
When the scenario reaches a natural conclusion, end the roleplay and give brief feedback:
- What they did well
- What they could improve
- Important points they missed
- One practical tip
If the employee asks for feedback earlier, end the roleplay and provide it.
RULES:
- Do not invent information not provided in the scenario or flashcard.
- Do not play both sides.
- Do not lecture.
- Do not give the ideal answer before the employee attempts it.
- Adapt the difficulty to the employee's performance.
- Keep spoken responses concise and natural.`;

const DEFAULT_SCENARIO_FIRST_MESSAGE = `Hi, {firstName}. I am Rafa. PerfectCheck's Server Performance AI Coach. Today we will cover the {scenario}. Are you ready to start?`;

const DEFAULT_PROMPT = `You are Rafa, a Food & Beverage Server Performance AI Coach training about the "{dishName}" dish. Ask questions about this specific dish based on these key points:
{flashcardContent}
Ask natural questions that help the server practice their sales pitch for this dish. Be curious about ingredients, preparation, flavor profile, and what makes it special.`;

const DEFAULT_FIRST_MESSAGE = `Hi, {firstName}. I am Rafa. PerfectCheck's Server Performance AI Coach. Today we will cover the {dishName}. Are you ready to start?`;

const htmlToPlainText = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").replace(/\s+\n/g, "\n").trim();
};

const replaceTemplateVars = (template, variables) => {
  let result = template || "";
  Object.keys(variables).forEach((key) => {
    result = result.split(`{${key}}`).join(variables[key] || "");
  });
  return result;
};

const PracticeVoiceModal = ({ isOpen, toggle, card, isScenario = false }) => {
  const [isStarting, setIsStarting] = useState(false);
  const [conversationStartTime, setConversationStartTime] = useState(null);

  const conversation = useConversation();

  const { userInfo } = useSelector((state) => ({
    userInfo: state.UserOutletGroup.userInfo,
  }));

  const endPractice = useCallback(async () => {
    await conversation.endSession().catch(() => {});
    setConversationStartTime(null);
  }, [conversation]);

  useEffect(() => {
    if (!isOpen) {
      conversation.endSession().catch(() => {});
      setConversationStartTime(null);
    }
  }, [isOpen]);

  if (!card) return null;

  const isScenarioPractice = isScenario || Number(card?.is_scenario) === 1;
  const dishName = card.item_name || card.flash_card_name || "";
  const scenarioName = dishName;
  const firstName = userInfo?.first_name || userInfo?.name || "User";

  const buildPrompt = (promptTemplate) => {
    const flashcardContent = htmlToPlainText(
      card.format_coching_script || card.coching_script || ""
    ).slice(0, 4000);
    const template = promptTemplate?.trim()
      ? promptTemplate
      : (isScenarioPractice ? DEFAULT_SCENARIO_PROMPT : DEFAULT_PROMPT);
    const prompt = replaceTemplateVars(template, {
      dishName,
      scenario: scenarioName,
      flashcardContent,
      firstName
    });
    return prompt.length > MAX_PROMPT_CHARS
      ? prompt.slice(0, MAX_PROMPT_CHARS)
      : prompt;
  };

  const buildFirstMessage = (msgTemplate) => {
    const template = msgTemplate?.trim()
      ? msgTemplate
      : (isScenarioPractice ? DEFAULT_SCENARIO_FIRST_MESSAGE : DEFAULT_FIRST_MESSAGE);
    return replaceTemplateVars(template, {
      dishName,
      scenario: scenarioName,
      flashcardContent: "",
      firstName
    });
  };

  const startPractice = async () => {
    try {
      setIsStarting(true);

      if (conversation.status === "connected" || conversation.status === "connecting") {
        await conversation.endSession().catch(() => {});
      }

      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStream.getTracks().forEach((track) => track.stop());

      const res = await postDataAPI({
        serviceName: "server_data",
        op: "agent_get_signed_url",
        is_scenario: isScenarioPractice ? 1 : 0
      });

      if (res.status !== "ok") {
        alert(res.message || "Failed to load agent!");
        return;
      }

      const signedUrl = res.data?.signedUrl || "";
      const promptTemplate = res.data?.prmoptTemplate || "";
      const msgTemplate = res.data?.msgTemplate || "";
      if (!signedUrl) throw new Error("No signed URL");

      const flashcardContent = htmlToPlainText(
        card.format_coching_script || card.coching_script || ""
      ).slice(0, 3500);

      await conversation.startSession({
        signedUrl,
        connectionType: "websocket",
        dynamicVariables: {
          scenario: scenarioName || "",
          flashcardContent: flashcardContent || "",
          firstName: firstName || "User",
          dishName: dishName || ""
        },
        overrides: {
          agent: {
            prompt: { prompt: buildPrompt(promptTemplate) },
            firstMessage: buildFirstMessage(msgTemplate)
          }
        }
      });
      setConversationStartTime(Date.now());
    } catch (err) {
      console.error(err);
      await conversation.endSession().catch(() => {});
      alert(err?.message || "Could not start practice");
    } finally {
      setIsStarting(false);
    }
  };

  const isActive =
    conversation.status === "connected" || conversation.status === "connecting";

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>
        🎙 {isScenarioPractice ? "Practice This Scenario" : "Practice Your Sales Pitch"}
        <div className="text-muted small">
          {isScenarioPractice
            ? "Roleplay this scenario with an AI guest"
            : "Practice selling this dish with an AI customer"}
        </div>
      </ModalHeader>

      <ModalBody className="text-center">
        <div className="border rounded p-3 mb-4">
          <small className="text-muted">Training on:</small>
          <h5 className="fw-bold mb-0">{dishName}</h5>
        </div>

        {!isActive ? (
          <>
            <i className="ri-mic-fill fs-1" />
            <div className="mb-3 text-muted">
              Click start to begin practice
            </div>

            <Button
              color="primary"
              className="w-100"
              onClick={startPractice}
              disabled={isStarting}
            >
              🎤 {isStarting ? "Starting..." : "Start Practice"}
            </Button>
          </>
        ) : (
          <>
            <i className="ri-mic-fill fs-1" />
            <div className="my-3 text-danger fw-medium">
              🔴 {conversation.isSpeaking ? "AI is speaking..." : "Listening..."}
            </div>

            <Button
              color="danger"
              className="w-100"
              onClick={endPractice}
            >
              ⏹ End Practice
            </Button>
          </>
        )}

        <div className="bg-light rounded p-3 mt-4 text-start">
          <small className="fw-medium">Tips for practice:</small>
          <ul className="mb-0 small text-muted">
            {isScenarioPractice ? (
              <>
                <li>Stay in character as the server</li>
                <li>Use the must-use phrases from the flashcard</li>
                <li>Respond naturally to the guest</li>
                <li>Use the scenario content as a guide</li>
              </>
            ) : (
              <>
                <li>Speak clearly about dish features</li>
                <li>Highlight unique selling points</li>
                <li>Answer customer questions naturally</li>
                <li>Use the flashcard content as a guide</li>
              </>
            )}
          </ul>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default PracticeVoiceModal;
